<template>
    <StepShell :embedded="embedded">
        <StepHeader
            :embedded="embedded"
            align="center"
            size="display"
            :title="t('onboarding.calendarFirst.assessmentIntroStep.title')"
            :lede="gapCopy"
            :source="embedded ? 'perspective' : undefined">
            <template #mark><PerspectiveMark /></template>
        </StepHeader>

        <NadiaCard :title="headlineCopy">
            <div>
                <p class="card-body">{{ t("onboarding.calendarFirst.assessmentIntroStep.pitch") }}</p>
                <!-- Describe the questions before the CTA so users know they are agreeing to an assessment.
                     -->
                <StepPoints :points="facts" variant="dot" />
            </div>
        </NadiaCard>

        <!-- The linear flow allows skipping the assessment to continue coaching without readings or a
             profile.
             -->
        <StepActions flush>
            <BaseButton v-if="skippable ?? !embedded" theme="tertiary" class="step-skip" @click="emit('skip')">{{ t("onboarding.calendarFirst.actions.skipForNow") }}</BaseButton>
            <BaseButton theme="primary" @click="emit('continue')">
                {{ ctaLabel }}
            </BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import PerspectiveMark from "~vue/icons/PerspectiveMark.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import NadiaCard from "./NadiaCard.vue";
import { formatHours } from "./scanStats";
import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import type { StepPoint } from "./StepPoints.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";
import type { CalendarScanStats } from "./types";

const {
    stats,
    managesPeople,
    questionCount,
    calendarSkipped,
    embedded = false,
    skippable = undefined,
    calendarLast = false,
} = defineProps<{
    embedded?: boolean;
    skippable?: boolean;
    calendarLast?: boolean;
    stats: CalendarScanStats | null;
    managesPeople: boolean;
    questionCount: number;
    /** True when there is no calendar behind this screen, so nothing can be read back. */
    calendarSkipped: boolean;
}>();

const emit = defineEmits<{ continue: []; skip: [] }>();

const { t } = useI18n();

/* Use the server's Perspectives question count so the CTA stays accurate when the question bank changes. */
const ctaLabel = computed(() =>
    calendarLast ? t("onboarding.profileSetup.calendarLast.perspective.cta") : t("onboarding.calendarFirst.assessmentIntroStep.cta", { count: questionCount }, questionCount),
);

/* Without a calendar there is no "week" to have seen, so the headline cannot
   claim one. These questions are then the only thing the flow has to go on. */
const headlineCopy = computed(() => {
    if (!calendarSkipped) {
        return t("onboarding.calendarFirst.assessmentIntroStep.headline.calendar");
    }
    return embedded ? t("onboarding.calendarFirst.assessmentIntroStep.headline.noCalendarEmbedded") : t("onboarding.calendarFirst.assessmentIntroStep.headline.noCalendar");
});

const facts = computed<StepPoint[]>(() => [
    { tone: "pink", lead: t("onboarding.calendarFirst.assessmentIntroStep.facts.sliders") },
    { tone: "amber", lead: t("onboarding.calendarFirst.assessmentIntroStep.facts.autosave") },
    calendarSkipped
        ? {
              tone: "teal",
              lead: embedded ? t("onboarding.calendarFirst.assessmentIntroStep.facts.noCalendarEmbedded") : t("onboarding.calendarFirst.assessmentIntroStep.facts.noCalendar"),
          }
        : { tone: "teal", lead: t("onboarding.calendarFirst.assessmentIntroStep.facts.reread") },
]);

/* The argument for the questions, so it sits immediately above them. */
const gapCopy = computed(() => {
    if (calendarLast) {
        return t("onboarding.profileSetup.calendarLast.perspective.lede", { count: questionCount });
    }
    const role = managesPeople ? "manager" : "ic";
    if (calendarSkipped) {
        return embedded
            ? t("onboarding.calendarFirst.assessmentIntroStep.gap.noCalendarEmbedded", { count: questionCount, role })
            : t("onboarding.calendarFirst.assessmentIntroStep.gap.noCalendar", { count: questionCount, role });
    }
    if (stats !== null && stats.hours_per_week > 0) {
        return t("onboarding.calendarFirst.assessmentIntroStep.gap.calendarWithLoad", { role, hours: formatHours(stats.hours_per_week) });
    }
    return t("onboarding.calendarFirst.assessmentIntroStep.gap.calendar", { role });
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .card-body {
        @apply body-small text-ink-muted;
    }
}
</style>
